import React, { useMemo, useRef, useState } from 'react';
import {
	Business,
	BusinessQuery,
	ContactType,
	List,
	SearchType,
	UserNotes
} from '@/modules/dashboard/dashboardTypes';

import { sendAnalytics } from '@/services/analyticsService';
import { XIcon } from '@/resources/assets/images';
import { Tabs } from '@/components/atoms/Tabs';
import type { DirectEmail } from '@/modules/email/emailTypes';
import type { User } from '@/modules/user/userTypes';
import { ComposeEmailModal } from '@/components/molecules/ComposeEmailModal';
import type { EmailGetEmailThreadProps } from '@/modules/email/emailActions';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { dashboardSetQuery } from '@/modules/dashboard/dashboardActions';
import type { RootState } from '@/rootReducer';
import Button from '@/components/Button/newButtons';
import {
	ReminderModal,
	ReminderModalProps
} from './src/components/atoms/ReminderModal';
import {
	AddContactDataModal,
	AddContactDataModalProps
} from './src/components/atoms/AddContactDataModal';
import {
	RemoveContactDataModal,
	type RemoveContactDataModalProps
} from './src/components/atoms/RemoveContactDataModal';
import {
	RemoveNoteModal,
	type RemoveNoteModalProps
} from './src/components/atoms/RemoveNoteModal';
import {
	ReportIncorrectDataModal,
	type ReportIncorrectDataModalProps
} from './src/components/atoms/ReportIncorrectDataModal';
import { BusinessInfoTab } from './src/components/molecules/BusinessInfoTab';
import { BusinessEmailsTab } from './src/components/molecules/BusinessEmailsTab';
import { BusinessActivityTab } from './src/components/molecules/BusinessActivityTab';
import { TagChip } from './src/components/atoms/TagChip';
import type {
	BusinessInfoTabHandles,
	BusinessInfoTabProps
} from './src/components/molecules/BusinessInfoTab/BusinessInfoTab';

type BusinessPreviewProps = Pick<
	BusinessInfoTabProps,
	'appliedFilters' | 'goToMap'
> &
	Pick<
		ReportIncorrectDataModalProps,
		| 'onReportIncorrectData'
		| 'isReportIncorrectDataSuccess'
		| 'isReportIncorrectDataError'
		| 'isReportIncorrectDataLoading'
	> &
	Pick<RemoveNoteModalProps, 'onRemoveNote'> &
	Pick<RemoveContactDataModalProps, 'onRemoveContactData'> &
	Pick<
		ReminderModalProps,
		| 'isTaskLoading'
		| 'isTaskError'
		| 'isTaskSuccess'
		| 'onDeleteReminder'
		| 'onResetReminder'
		| 'onSetReminder'
	> &
	Pick<
		AddContactDataModalProps,
		| 'onAddContactData'
		| 'isContactDataError'
		| 'isContactDataLoading'
		| 'isContactDataSuccess'
	> & {
		listId?: List['id'];
		user: User;
		selectedBusiness: Business | null;
		onSaveNote: (
			businessId: string,
			businessName: string,
			notes: UserNotes
		) => void;
		onSelectBusiness: (business: Business | null) => void;
		onGetEmailThread: ({
			businessId,
			userEmail
		}: EmailGetEmailThreadProps) => void;
		lastBusinessIdRef?: React.MutableRefObject<string | null>;
	};

export const BusinessPreview = ({
	listId,
	user,
	selectedBusiness,
	onReportIncorrectData,
	isReportIncorrectDataSuccess,
	isReportIncorrectDataError,
	isReportIncorrectDataLoading,
	onSaveNote,
	onSelectBusiness,
	onGetEmailThread,
	onAddContactData,
	isContactDataError,
	isContactDataLoading,
	isContactDataSuccess,
	isTaskLoading,
	isTaskError,
	isTaskSuccess,
	onDeleteReminder,
	onResetReminder,
	onSetReminder,
	onRemoveNote,
	onRemoveContactData,
	lastBusinessIdRef,
	appliedFilters,
	goToMap
}: BusinessPreviewProps) => {
	const businessInfoRef = useRef<BusinessInfoTabHandles>(null);

	const [query] = useSelector<RootState, [BusinessQuery]>(
		state => [state.dashboard.get('query').toJS()],
		shallowEqual
	);

	const dispatch = useDispatch();
	const [selectedTab, setSelectedTab] = useState('info');

	const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
	const [customContactDataIndex, setCustomContactDataIndex] = useState<
		number | undefined
	>(undefined);
	const [isReportDataModalOpen, setIsReportDataModalOpen] = useState(false);
	const [isAddDataModalOpen, setIsAddDataModalOpen] = useState(false);
	const [isRemoveDataModalOpen, setIsRemoveDataModalOpen] = useState(false);
	const [directEmail, setDirectEmail] = useState<DirectEmail | undefined>(
		undefined
	);
	const [isComposeEmailModalOpen, setIsComposeEmailModalOpen] =
		useState(false);
	const [isRemoveNoteModalOpen, setIsRemoveNoteModalOpen] = useState(false);

	const setQuery = (q: BusinessQuery) =>
		dispatch(dashboardSetQuery({ ...q, user }));

	const userDataOptions = useMemo(
		() =>
			[
				'',
				selectedBusiness?.email &&
				selectedBusiness?.emailLowPriority !== true
					? selectedBusiness?.email
					: undefined,

				selectedBusiness?.facebook?.email &&
				selectedBusiness?.facebook?.emailLowPriority !== true &&
				selectedBusiness?.facebook?.email !== selectedBusiness?.email
					? selectedBusiness?.facebook.email
					: undefined,

				selectedBusiness?.web?.email &&
				selectedBusiness?.web?.email !== selectedBusiness?.email &&
				selectedBusiness?.web?.emailLowPriority !== true &&
				selectedBusiness?.web?.email !==
					selectedBusiness?.facebook?.email
					? selectedBusiness?.web.email
					: undefined,

				selectedBusiness?.facebook?.messengerUrl
					? 'Facebook Messenger'
					: undefined,

				selectedBusiness?.socials?.instagram
					? 'Instagram Messenger'
					: undefined,

				selectedBusiness?.phone?.phone || undefined
			].filter(
				(item): item is string =>
					typeof item === 'string' && Boolean(item)
			),
		[
			selectedBusiness?.email,
			selectedBusiness?.emailLowPriority,
			selectedBusiness?.facebook?.email,
			selectedBusiness?.facebook?.emailLowPriority,
			selectedBusiness?.facebook?.messengerUrl,
			selectedBusiness?.phone?.phone,
			selectedBusiness?.socials?.instagram,
			selectedBusiness?.web?.email,
			selectedBusiness?.web?.emailLowPriority
		]
	);

	const handleRemoveBusinessSelection = () => {
		onSelectBusiness(null);
	};

	const handleOpenReminderModal = () => {
		setIsReminderModalOpen(true);
	};

	const handleCloseReminderModal = () => {
		setIsReminderModalOpen(false);
	};

	const handleOpenAddDataModal = () => {
		setIsAddDataModalOpen(true);
	};

	const handleCloseAddDataModal = () => {
		setIsAddDataModalOpen(false);
	};

	const handleOpenRemoveDataModal = (index: number) => {
		setIsRemoveDataModalOpen(true);
		setCustomContactDataIndex(index);
	};

	const handleOpenRemoveNoteModal = () => {
		setIsRemoveNoteModalOpen(true);
	};

	const handleCloseRemoveNoteModal = () => {
		setIsRemoveNoteModalOpen(false);
	};

	const handleCloseRemoveDataModal = () => {
		setIsRemoveDataModalOpen(false);
	};

	const handleOpenComposeEmail = ({ email }: Pick<DirectEmail, 'email'>) => {
		if (!selectedBusiness) return;

		setDirectEmail({ business: selectedBusiness, email });

		setIsComposeEmailModalOpen(true);

		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: 'User opened a direct email',
			properties: {
				businessName: selectedBusiness?.name,
				businessId: selectedBusiness?._id || selectedBusiness?.id,
				businessAddress: selectedBusiness?.address
			}
		});
	};

	const handleCloseComposeEmailModal = () => {
		setIsComposeEmailModalOpen(false);
	};

	const handleOpenReportDataModal = () => {
		setIsReportDataModalOpen(true);
	};

	const handleCloseReportDataModal = () => {
		setIsReportDataModalOpen(false);
	};

	const handleSetAsContacted = (key: string) => {
		if (!selectedBusiness) return;

		const businessId =
			selectedBusiness?._id ||
			selectedBusiness?.businessId ||
			selectedBusiness?.id;
		if (!businessId) return;

		const keyIndex = Number(key);
		const isUserAddedData = !Number.isNaN(keyIndex);

		const userAddedData = selectedBusiness?.userAddedData
			? selectedBusiness?.userAddedData.map((element, i) => {
					if (isUserAddedData && i === keyIndex) {
						return { ...element, checked: true };
					}
					return element;
				})
			: [];

		const contactType = Object.keys(
			selectedBusiness?.contactType || {}
		).reduce((acc, currKey) => {
			const typedKey = currKey as keyof ContactType;

			acc[typedKey] =
				!isUserAddedData && currKey === key ? true : acc?.[typedKey];

			return acc;
		}, selectedBusiness?.contactType || {});

		onSaveNote?.(businessId, selectedBusiness?.name || '', {
			isContacted:
				userAddedData?.some(data => data.checked) ||
				Object.values(contactType).some(Boolean),
			lead: selectedBusiness?.lead,
			note: selectedBusiness?.note ?? '',
			contactType,
			userAddedData: userAddedData ?? ''
		});

		sendAnalytics({
			type: 'track',
			eventName: isUserAddedData
				? 'User contacted a business via their User Added Data'
				: `User contacted a business via their: ${key.toUpperCase()}`,
			properties: {
				businessName: selectedBusiness?.name,
				businessId: selectedBusiness?._id || selectedBusiness?.id,
				businessAddress: selectedBusiness?.address
			}
		});
	};

	const handleEmailThread = (business: Business) => {
		if (user && user.email) {
			const businessId = (business._id ||
				business.businessId ||
				business.id) as string;

			onGetEmailThread({
				businessId,
				userEmail: user.email
			});
		}
	};

	const handleSelectTab = (tabValue: string) => {
		setSelectedTab(tabValue);

		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: `Opened the ${tabValue} tab`,
				properties: {
					businessId:
						selectedBusiness?.businessId || selectedBusiness?._id,
					businessName: selectedBusiness?.name
				}
			});
		}
	};

	const renderEmailsTab = () => {
		if (!selectedBusiness) return;
		if (selectedBusiness?._id !== lastBusinessIdRef?.current) {
			if (lastBusinessIdRef) {
				// eslint-disable-next-line no-param-reassign
				lastBusinessIdRef.current = selectedBusiness?._id;
			}
			handleEmailThread(selectedBusiness);
		}

		return (
			<BusinessEmailsTab
				business={selectedBusiness}
				openComposeEmail={handleOpenComposeEmail}
				getEmailThread={handleEmailThread}
				onSelectTab={handleSelectTab}
			/>
		);
	};

	const handleClickReadMoreCategories = () => {
		setSelectedTab('info');
		businessInfoRef.current?.onHighlightCategories();
	};

	const isSelectedBusinessEmpty =
		!selectedBusiness || Object.keys(selectedBusiness).length === 0;

	const parsedCategories = useMemo(() => {
		if (
			!selectedBusiness?.categories?.search ||
			selectedBusiness?.categories?.search.length === 0
		)
			return [];

		const categoriesWithStatus = selectedBusiness?.categories.search.map(
			category => {
				const categoryFound = appliedFilters?.searches?.find(
					search =>
						search.type === SearchType.INCLUDES &&
						search.value === category
				);

				const isActive = !!categoryFound?.value;

				return {
					category,
					isActive
				};
			}
		);

		return categoriesWithStatus.sort(
			(a, b) => Number(b.isActive) - Number(a.isActive)
		);
	}, [appliedFilters?.searches, selectedBusiness?.categories?.search]);

	const firstBusinessCategores = useMemo(() => {
		const maxCharacters = 40;

		const categories = parsedCategories.reduce<typeof parsedCategories>(
			(acc, curr) => {
				const accCharacterCount = acc.reduce(
					(sum, item) => sum + item.category.length,
					0
				);

				if (accCharacterCount + curr.category.length <= maxCharacters) {
					acc.push(curr);
				}
				return acc;
			},
			[]
		);

		return categories || [];
	}, [parsedCategories]);

	if (isSelectedBusinessEmpty) return null;

	return (
		<>
			<section className="absolute left-0 top-0 h-full w-full overflow-auto">
				<Tabs.Root value={selectedTab} onValueChange={setSelectedTab}>
					<header className="flex flex-col bg-dm-black pt-5">
						<button
							type="button"
							onClick={handleRemoveBusinessSelection}
							className="absolute right-2 top-3"
						>
							<XIcon className="[&_path]:fill-dm-white" />
						</button>

						<section className="flex items-center justify-between gap-5 p-6 text-dm-white">
							<div className="relative mr-6 flex aspect-square h-20 items-center justify-center md:h-[7.5rem]">
								{selectedBusiness?.yelp?.thumnnailURL ? (
									<img
										src={
											selectedBusiness?.yelp?.thumnnailURL
										}
										alt="Business Logo"
										className="h-full w-full rounded-full border border-dm-white object-cover"
									/>
								) : (
									<span className="flex h-full w-full items-center justify-center rounded-full bg-orange-opaque text-5xl font-bold text-dm-white">
										{selectedBusiness?.name?.[0].toUpperCase() +
											selectedBusiness?.name?.[1].toUpperCase()}
									</span>
								)}

								<span className="absolute right-0 top-0 flex aspect-square h-12 w-12 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full border-4 border-dm-gold bg-dm-black3 p-2 text-center text-base font-semibold leading-[3rem] text-dm-gold md:h-14 md:w-14 md:text-lg lg:text-xl">
									{typeof selectedBusiness?.score === 'number'
										? Math.floor(selectedBusiness?.score)
										: 0}
								</span>
							</div>

							<div className="flex w-full flex-col items-center justify-between gap-2 md:gap-4">
								<header className="ml-[0.5625rem] flex w-full flex-col gap-1">
									<h2 className="text-base font-semibold lg:text-lg">
										{selectedBusiness?.name}
									</h2>
									<p className="text-sm font-semibold">
										{selectedBusiness?.address?.city},{' '}
										{selectedBusiness?.address?.state}
									</p>
								</header>

								{firstBusinessCategores.length > 0 && (
									<ul className="flex w-full flex-wrap gap-2 text-sm md:text-base lg:text-lg 2xl:text-xl">
										{firstBusinessCategores.map(
											({ category, isActive }) => (
												<li key={category}>
													<TagChip
														isActive={isActive}
													>
														{category}
													</TagChip>
												</li>
											)
										)}

										<li>
											<Button
												onClick={
													handleClickReadMoreCategories
												}
												variant="text"
												className="h-full"
											>
												Read more...
											</Button>
										</li>
									</ul>
								)}
							</div>
						</section>

						<Tabs.List className="w-full pt-3.5" marginLess>
							<Tabs.Trigger value="info">Info</Tabs.Trigger>
							<Tabs.Trigger value="emails">Emails</Tabs.Trigger>
							<Tabs.Trigger value="activity">
								Activity
							</Tabs.Trigger>
						</Tabs.List>
					</header>

					<Tabs.Content
						value="info"
						className="relative h-full overflow-hidden"
					>
						<BusinessInfoTab
							appliedFilters={appliedFilters}
							selectBusiness={selectedBusiness}
							openSetReminder={handleOpenReminderModal}
							openAddDataModal={handleOpenAddDataModal}
							onSetAsContacted={handleSetAsContacted}
							openComposeEmail={handleOpenComposeEmail}
							saveNote={onSaveNote}
							openDeleteDataModal={handleOpenRemoveDataModal}
							openReportIncorrectDataModal={
								handleOpenReportDataModal
							}
							categories={parsedCategories}
							goToMap={goToMap}
							ref={businessInfoRef}
						/>
					</Tabs.Content>

					<Tabs.Content value="emails">
						{renderEmailsTab()}
					</Tabs.Content>

					<Tabs.Content value="activity">
						<BusinessActivityTab
							openOnEmail={handleOpenComposeEmail}
							openDeleteNoteModal={handleOpenRemoveNoteModal}
							onSelectTab={handleSelectTab}
						/>
					</Tabs.Content>
				</Tabs.Root>
			</section>

			{customContactDataIndex !== undefined && (
				<RemoveContactDataModal
					userEmail={user.email}
					businessId={selectedBusiness?._id}
					listId={listId}
					dataInd={customContactDataIndex}
					isOpen={isRemoveDataModalOpen}
					onCloseModal={handleCloseRemoveDataModal}
					onRemoveContactData={onRemoveContactData}
				/>
			)}

			<RemoveNoteModal
				isOpen={isRemoveNoteModalOpen}
				onCloseModal={handleCloseRemoveNoteModal}
				onRemoveNote={onRemoveNote}
			/>

			<AddContactDataModal
				businessUserAddedData={selectedBusiness?.userAddedData}
				businessName={selectedBusiness?.name}
				businesslistId={selectedBusiness?.listId}
				businessId={selectedBusiness?._id}
				userEmail={user.email}
				isOpen={isAddDataModalOpen}
				onCloseModal={handleCloseAddDataModal}
				onAddContactData={onAddContactData}
				isContactDataError={isContactDataError}
				isContactDataLoading={isContactDataLoading}
				isContactDataSuccess={isContactDataSuccess}
			/>

			<ReportIncorrectDataModal
				businessId={selectedBusiness?._id}
				isOpen={isReportDataModalOpen}
				onCloseModal={handleCloseReportDataModal}
				userEmail={user.email}
				userDataOptions={userDataOptions}
				onReportIncorrectData={onReportIncorrectData}
				isReportIncorrectDataSuccess={isReportIncorrectDataSuccess}
				isReportIncorrectDataError={isReportIncorrectDataError}
				isReportIncorrectDataLoading={isReportIncorrectDataLoading}
			/>

			<ReminderModal
				isOpen={isReminderModalOpen}
				onCloseModal={handleCloseReminderModal}
				isTaskLoading={isTaskLoading}
				isTaskError={isTaskError}
				isTaskSuccess={isTaskSuccess}
				onDeleteReminder={onDeleteReminder}
				onResetReminder={onResetReminder}
				onSetReminder={onSetReminder}
				hasReminderTaskSet={
					selectedBusiness?.reminder?.hasReminderTaskSet
				}
				reminderTriggerDate={
					selectedBusiness?.reminder?.reminderTriggerDate
				}
				reminderText={selectedBusiness?.reminder?.reminderText}
				businessId={selectedBusiness?._id}
				businessName={selectedBusiness?.name}
				listId={selectedBusiness?.listId}
				originalNote={selectedBusiness?.note}
				userEmail={user.email}
				userTimeZone={user.timeZone}
			/>

			<ComposeEmailModal
				isOpen={isComposeEmailModalOpen}
				onCloseModal={handleCloseComposeEmailModal}
				businessSelected={selectedBusiness}
				directEmail={directEmail}
				listId={listId}
				userEmail={user.email}
				appliedFilters={query}
				onFilterChange={setQuery}
			/>
		</>
	);
};
